import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navigation } from "./Navigation";
import { Footer } from "./Footer";
import { Reveal } from "./motion-primitives";
import { RecommendationCard } from "./RecommendationComponents";
import { fetchRecommendations } from "./libraryApi";
import { useSeo } from "./useSeo";

// Da Nang / Ho Chi Minh City edits — powered entirely by the Knowledge Library.
export const DESTINATION_EDITS = {
  "da-nang": {
    destination: "Da Nang",
    country: "Vietnam",
    eyebrow: "Vietnam · The Edit",
    title: "Da Nang",
    tagline: "Vietnam's quiet coast — an edit we're building slowly, and only from places we've stood in ourselves.",
    heroImage: "https://images.unsplash.com/photo-1558002890-c0b30998d1e6?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    heroAlt: "Da Nang coastline with the river and city beyond (placeholder image)",
    growingCopy:
      "Our Da Nang edit is growing. These are places we've personally experienced and would currently put on your radar.",
    seo: {
      title: "Da Nang — The Holideviser Edit | Holideviser",
      description:
        "A growing, personally verified Holideviser edit of Da Nang. Hand-picked places we've experienced ourselves, with our point of view.",
    },
  },
  "ho-chi-minh-city": {
    destination: "Ho Chi Minh City",
    country: "Vietnam",
    eyebrow: "Vietnam · The Edit",
    title: "Ho Chi Minh City",
    tagline: "The country's loudest, fastest city — we're starting our edit with places we've experienced first-hand.",
    heroImage: "https://images.unsplash.com/photo-1578768124932-e0cc82f7a37e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    heroAlt: "Ho Chi Minh City skyline at dusk along the river (placeholder image)",
    growingCopy:
      "Our Ho Chi Minh City edit is growing. We're starting with places we've personally experienced.",
    seo: {
      title: "Ho Chi Minh City — The Holideviser Edit | Holideviser",
      description:
        "A growing, personally verified Holideviser edit of Ho Chi Minh City. Places we've experienced ourselves, with our point of view.",
    },
  },
  goa: {
    destination: "Goa",
    country: "India",
    eyebrow: "India · The Edit",
    title: "Goa",
    tagline: "Local knowledge, built from the inside — an edit we're starting with places we know firsthand.",
    heroImage: "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    heroAlt: "Goa coastline at golden hour (placeholder image)",
    growingCopy: "Our Goa edit is growing. We're starting with places we know firsthand.",
    seo: {
      title: "Goa — The Holideviser Edit | Holideviser",
      description:
        "A growing, personally verified Holideviser edit of Goa, India. Places we know firsthand, with our point of view.",
    },
  },
};

export const DestinationEditPage = ({ slug }) => {
  const config = DESTINATION_EDITS[slug];
  useSeo(config.seo);

  const [recs, setRecs] = useState(null);

  useEffect(() => {
    let active = true;
    fetchRecommendations({ destination: config.destination })
      .then((data) => active && setRecs(data))
      .catch(() => active && setRecs([]));
    return () => {
      active = false;
    };
  }, [config.destination]);

  return (
    <div className="min-h-screen bg-ink text-cream" data-testid={`destination-edit-${slug}`}>
      <Navigation />

      {/* Hero */}
      <header className="relative overflow-hidden border-b border-charcoal">
        <div className="absolute inset-0">
          <img src={config.heroImage} alt={config.heroAlt} className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
        </div>
        <div className="relative mx-auto max-w-[1100px] px-6 pb-16 pt-40 sm:px-10 sm:pb-20 sm:pt-48">
          <Reveal>
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-teal">{config.eyebrow}</span>
          </Reveal>
          <Reveal delay={0.06}>
            <h1 className="mt-5 font-serif text-5xl font-light leading-none tracking-tight text-cream sm:text-7xl">
              {config.title}
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-6 max-w-xl font-sans text-lg leading-relaxed text-sand">{config.tagline}</p>
          </Reveal>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-6 py-16 sm:px-10 sm:py-24">
        {/* Holideviser Picks */}
        <div className="flex flex-col gap-3 border-b border-charcoal pb-8">
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-teal">Holideviser Picks</span>
          <h2 className="font-serif text-3xl font-light text-cream sm:text-4xl">
            Places we&apos;d put on your radar
          </h2>
          <p className="max-w-2xl font-sans text-sm leading-relaxed text-sand" data-testid="growing-copy">
            {config.growingCopy}
          </p>
        </div>

        {recs === null ? (
          <p className="mt-12 font-sans text-sm text-sand">Loading the edit…</p>
        ) : recs.length === 0 ? (
          <p className="mt-12 font-sans text-sm text-sand">This edit is just getting started. Check back soon.</p>
        ) : (
          <div className="mt-12 flex flex-col gap-12" data-testid="picks-list">
            {recs.map((rec, i) => (
              <Reveal key={rec.id} delay={i * 0.05}>
                <RecommendationCard rec={rec} defaultOpen={recs.length === 1} />
              </Reveal>
            ))}
          </div>
        )}

        {/* Plan CTA */}
        <div className="mt-20 border-t border-charcoal pt-16 text-center">
          <p className="mx-auto max-w-xl font-serif text-2xl font-light leading-relaxed text-cream sm:text-3xl">
            Want the whole trip planned with this kind of judgment?
          </p>
          <Link
            to={`/plan-my-trip?destination=${encodeURIComponent(config.destination)}`}
            data-testid="edit-plan-cta"
            className="mt-8 inline-block rounded-none bg-teal px-8 py-4 font-sans text-sm uppercase tracking-[0.18em] text-ink transition-colors hover:bg-cream"
          >
            Plan my trip
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
};
